import { getAuthToken } from '../utils/authUtils';

const API_BASE_URL = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Get active bed assignments for a facility
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Object>} Active bed assignments
 */
export const getActiveBedAssignments = async (facilityId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/bed-assignments?facilityId=${facilityId}&status=active`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get bed assignments: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting bed assignments:', error);
    throw error;
  }
};

/**
 * Get active room assignments for a facility
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Object>} Active room assignments
 */
export const getActiveRoomAssignments = async (facilityId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/room-assignments?facilityId=${facilityId}&status=active`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get room assignments: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting room assignments:', error);
    throw error;
  }
};

/**
 * Get active encounters for a facility
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Object>} Active encounters
 */
export const getActiveEncounters = async (facilityId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/encounters?facilityId=${facilityId}&status=active,in-progress,admitted`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get active encounters: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting active encounters:', error);
    throw error;
  }
};

/**
 * Get patient details
 * @param {string} patientId - The patient ID
 * @returns {Promise<Object|null>} Patient data or null if error
 */
export const getPatient = async (patientId) => {
  try {
    const { getPatient: getPatientFn } = await import('./patientService');
    return await getPatientFn(patientId);
  } catch (error) {
    console.error('Error getting patient:', error);
    return null; // Return null for any errors to make the API more resilient
  }
};

/**
 * Get bed assignments by facility (for backward compatibility)
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Array>} Bed assignments array
 */
export const getBedAssignmentsByFacility = async (facilityId) => {
  const result = await getActiveBedAssignments(facilityId);
  return result.bedAssignments?.map(assignment => ({
    id: assignment.id,
    ...assignment,
    type: 'bed'
  })) || [];
};

/**
 * Get room assignments by facility (for backward compatibility)
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Array>} Room assignments array
 */
export const getRoomAssignmentsByFacility = async (facilityId) => {
  const result = await getActiveRoomAssignments(facilityId);
  return result.roomAssignments?.map(assignment => ({
    id: assignment.id,
    ...assignment,
    type: 'room'
  })) || [];
};

/**
 * Get active encounters by facility (for backward compatibility)
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Array>} Encounters array
 */
export const getActiveEncountersByFacility = async (facilityId) => {
  const result = await getActiveEncounters(facilityId);
  return result.encounters || [];
};

/**
 * Get patient by ID (for backward compatibility)
 * @param {string} patientId - The patient ID
 * @returns {Promise<Object|null>} Patient data or null if error
 */
export const getPatientById = async (patientId) => {
  const result = await getPatient(patientId);
  return result?.patient || null;
};

/**
 * Get current bed assignment for a patient
 * @param {string} patientId - The patient ID
 * @returns {Promise<Object|null>} Current bed assignment or null if not found
 */
export const getCurrentBedAssignment = async (patientId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/bed-assignments/patient/${patientId}/current`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to get current bed assignment: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting current bed assignment:', error);
    return null;
  }
};

/**
 * Get current room assignment for a patient
 * @param {string} patientId - The patient ID
 * @returns {Promise<Object|null>} Current room assignment or null if not found
 */
export const getCurrentRoomAssignment = async (patientId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/room-assignments/patient/${patientId}/current`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to get current room assignment: ${response.statusText}`);
    }

    const data = await response.json();
    return data.assignment;
  } catch (error) {
    console.error('Error getting current room assignment:', error);
    return null;
  }
};

/**
 * Get available beds for a facility
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Object>} Available beds
 */
export const getAvailableBeds = async (facilityId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/beds/available?facilityId=${facilityId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get available beds: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error getting available beds:', error);
    return { availableBeds: [] };
  }
};

/**
 * Get available standalone rooms for a facility
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Object>} Available rooms
 */
export const getAvailableStandaloneRooms = async (facilityId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/rooms/available?facilityId=${facilityId}&type=standalone`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get available rooms: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error getting available rooms:', error);
    return { availableRooms: [] };
  }
};

/**
 * Discharge patient from bed
 * @param {string} assignmentId - The assignment ID
 * @param {string} dischargedBy - The user discharging the patient
 * @param {string} dischargedByRole - The role of the user discharging the patient
 * @param {string} notes - Optional notes for the discharge
 * @returns {Promise<Object>} Discharged patient data
 */
export const dischargePatientFromBed = async (assignmentId, dischargedBy, dischargedByRole, notes = '') => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/bed-assignments/${assignmentId}/discharge`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ notes, dischargedBy, dischargedByRole })
    });

    if (!response.ok) {
      throw new Error(`Failed to discharge patient from bed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error discharging patient from bed:', error);
    throw error;
  }
};

/**
 * Discharge patient from room
 * @param {string} assignmentId - The assignment ID
 * @param {string} dischargedBy - The user discharging the patient
 * @param {string} dischargedByRole - The role of the user discharging the patient
 * @param {string} notes - Optional notes for the discharge
 * @returns {Promise<Object>} Discharged patient data
 */
export const dischargePatientFromRoom = async (assignmentId, dischargedBy, dischargedByRole, notes = '') => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/room-assignments/${assignmentId}/discharge`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ notes, dischargedBy, dischargedByRole })
    });

    if (!response.ok) {
      throw new Error(`Failed to discharge patient from room: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error discharging patient from room:', error);
    throw error;
  }
};

/**
 * Transfer patient to a different bed
 * @param {string} assignmentId - The assignment ID
 * @param {string} newBedId - The ID of the new bed
 * @param {string} transferredBy - The user transferring the patient
 * @param {string} transferredByRole - The role of the user transferring the patient
 * @param {string} notes - Optional notes for the transfer
 * @returns {Promise<Object>} Transferred patient data
 */
export const transferPatientToBed = async (assignmentId, newBedId, transferredBy, transferredByRole, notes = '') => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/bed-assignments/${assignmentId}/transfer`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        newBedId, 
        transferredBy, 
        transferredByRole, 
        notes 
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to transfer patient to bed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error transferring patient to bed:', error);
    throw error;
  }
};

/**
 * Assign a patient to a bed
 * @param {Object} bedAssignmentData - The data for assigning a patient to a bed
 * @returns {Promise<Object>} Assigned patient data
 */
export const assignBedToPatient = async (bedAssignmentData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/bed-assignments`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(bedAssignmentData)
    });

    if (!response.ok) {
      throw new Error(`Failed to assign patient to bed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error assigning patient to bed:', error);
    throw error;
  }
};
